import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, ChevronRight, ChevronDown, CheckCircle2, AlertTriangle,
  CalendarDays, ShieldCheck, Globe2, HelpCircle, Compass, CheckSquare,
  Wand2, Camera, FileText, Sparkles, Ticket, BookOpen, ArrowLeftRight,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";
import { useAnalytics, EVENTS } from "../hooks/useAnalytics";

const eligIcons = [
  { icon: Globe2, color: "bg-sky-50 text-sky-700" },
  { icon: BookOpen, color: "bg-violet-50 text-violet-700" },
  { icon: ShieldCheck, color: "bg-teal-50 text-teal-700" },
  { icon: Camera, color: "bg-amber-50 text-amber-700" },
];

const dateColors = [
  "bg-teal-50 border-teal-200 text-teal-700",
  "bg-sky-50 border-sky-200 text-sky-700",
  "bg-violet-50 border-violet-200 text-violet-700",
];

export default function GreenCardLotteryPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent } = useAnalytics();
  const [openFaq, setOpenFaq] = useState(null);

  const highlights = t("dvl.whatHighlights", { returnObjects: true });
  const dates = t("dvl.dates", { returnObjects: true });
  const eligReqs = t("dvl.eligReqs", { returnObjects: true });
  const howSteps = t("dvl.howSteps", { returnObjects: true });
  const mistakes = t("dvl.mistakes", { returnObjects: true });
  const checklistItems = t("dvl.checklistItems", { returnObjects: true });
  const compHeaders = t("dvl.compHeaders", { returnObjects: true });
  const compRows = t("dvl.compRows", { returnObjects: true });
  const faqs = t("dvl.faqs", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background" data-testid="gc-lottery-page">
      <SEOHead
        title={`${t("dvl.title")} ${t("dvl.titleHighlight")}`}
        description={t("dvl.subtitle")}
      />

      {/* 1. HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dvl-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-5 font-medium">
            <Ticket className="h-3.5 w-3.5 mr-1.5" />
            {t("dvl.badge")}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            {t("dvl.title")}{" "}<span className="gradient-text">{t("dvl.titleHighlight")}</span>
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            {t("dvl.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#eligibility" data-testid="dvl-hero-elig">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base"
                onClick={() => trackEvent(EVENTS.CHECK_ELIGIBILITY, { source: "lottery_hero" })}>
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvl.ctaEligibility")}
              </Button>
            </a>
            <Link to={lp("/auth?mode=register")} data-testid="dvl-hero-start">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base"
                onClick={() => trackEvent(EVENTS.START_FREE_GUIDE, { source: "lottery_hero" })}>
                {t("dvl.ctaStart")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvl-what-is">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 mb-5">{t("dvl.whatTitle")}</h2>
          <div className="text-base leading-relaxed text-slate-600 space-y-4 mb-8">
            <p>{t("dvl.whatP1")}</p>
            <p>{t("dvl.whatP2")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.isArray(highlights) && highlights.map((h, i) => (
              <Card key={i} className="border-slate-100 shadow-sm" data-testid={`dvl-highlight-${i}`}>
                <CardContent className="p-5 text-center">
                  <p className="text-2xl font-bold text-teal-700 mb-1">{h.num}</p>
                  <p className="text-sm text-slate-600">{h.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 3. KEY DATES */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvl-dates">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.datesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.datesTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {Array.isArray(dates) && dates.map((d, i) => (
              <Card key={i} className={`border shadow-sm ${dateColors[i]}`} data-testid={`dvl-date-${i}`}>
                <CardContent className="p-6">
                  <CalendarDays className="h-7 w-7 mb-3" />
                  <p className="text-lg font-bold text-slate-900 mb-1">{d.period}</p>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">{d.title}</h3>
                  <p className="text-sm text-slate-600">{d.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/gc/green-card-timeline")} data-testid="dvl-dates-cta">
              <Button variant="outline" className="font-medium border-slate-200">
                <CalendarDays className="mr-2 h-4 w-4 text-teal-600" /> {t("dvl.ctaTimeline")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. WHO CAN APPLY */}
      <section id="eligibility" className="py-14 md:py-20 bg-white" data-testid="dvl-eligibility">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.eligLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.eligTitle")}</h2>
            <p className="mt-3 text-base text-slate-600 max-w-2xl mx-auto">{t("dvl.eligIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {Array.isArray(eligReqs) && eligReqs.map((r, i) => {
              const meta = eligIcons[i] || eligIcons[0];
              return (
                <Card key={i} className="border-slate-100 shadow-sm hover:shadow-md transition-all duration-300" data-testid={`dvl-elig-${i}`}>
                  <CardContent className="p-6">
                    <div className={`h-11 w-11 rounded-xl ${meta.color} flex items-center justify-center mb-4`}>
                      <meta.icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mb-2">{r.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{r.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fforms")}>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="dvl-elig-cta">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvl.ctaEligibility")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. HOW TO APPLY */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvl-how-to-apply">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.howLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.howTitle")}</h2>
          </div>
          <div className="space-y-0">
            {Array.isArray(howSteps) && howSteps.map((s, i) => (
              <div key={i} className="flex gap-4 md:gap-6" data-testid={`dvl-step-${i + 1}`}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-teal-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</div>
                  {i < howSteps.length - 1 && <div className="w-0.5 flex-1 bg-teal-200 my-1" />}
                </div>
                <div className={i < howSteps.length - 1 ? "pb-8" : ""}>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")}>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="dvl-how-cta">
                <CheckSquare className="mr-2 h-4 w-4" /> {t("dvl.ctaChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. COMMON MISTAKES */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvl-mistakes">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.mistakesLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.mistakesTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.isArray(mistakes) && mistakes.map((m, i) => (
              <Card key={i} className="border-amber-200/60 bg-amber-50/30 shadow-sm" data-testid={`dvl-mistake-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{m.title}</h3>
                      <p className="text-sm text-slate-600">{m.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fdocuments")}>
              <Button variant="outline" className="font-medium border-slate-200" data-testid="dvl-mistake-cta">
                <Sparkles className="mr-2 h-4 w-4 text-teal-600" /> {t("dvl.ctaCheckDocs")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CHECKLIST */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvl-checklist">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.checklistLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.checklistTitle")}</h2>
            <p className="mt-3 text-base text-slate-600">{t("dvl.checklistIntro")}</p>
          </div>
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="p-5 md:p-8">
              <div className="space-y-3">
                {Array.isArray(checklistItems) && checklistItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`dvl-check-${i}`}>
                    <CheckCircle2 className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="mt-8 text-center">
            <Link to={lp("/auth?mode=register&redirect=%2Fchecklists")}>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="dvl-check-cta">
                <CheckSquare className="mr-2 h-4 w-4" /> {t("dvl.ctaGenChecklist")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. COMPARISON TABLE */}
      <section className="py-14 md:py-20 bg-white" data-testid="dvl-comparison">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.compLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.compTitle")}</h2>
            <p className="mt-3 text-base text-slate-600">{t("dvl.compIntro")}</p>
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card className="border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full" data-testid="dvl-comp-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {Array.isArray(compHeaders) && compHeaders.map((h, i) => (
                      <th key={i} className={`px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider ${i === 0 ? "text-slate-500" : "text-slate-700"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(compRows) && compRows.map((row, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      {row.map((cell, j) => (
                        <td key={j} className={`px-5 py-3.5 text-sm ${j === 0 ? "font-medium text-slate-700" : "text-slate-600"} ${j === 1 ? "font-semibold text-teal-700" : ""}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-4">
            {Array.isArray(compRows) && compRows.map((row, i) => (
              <Card key={i} className="border-slate-100 shadow-sm" data-testid={`dvl-comp-mobile-${i}`}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{row[0]}</p>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {Array.isArray(compHeaders) && compHeaders.slice(1).map((h, j) => (
                      <div key={j}>
                        <p className="text-xs text-slate-400 mb-0.5">{h}</p>
                        <p className={`font-medium ${j === 0 ? "text-teal-700" : "text-slate-700"}`}>{row[j + 1]}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to={lp("/gc")} data-testid="dvl-comp-cta">
              <Button variant="outline" className="font-medium border-slate-200">
                <BookOpen className="mr-2 h-4 w-4 text-teal-600" /> {t("dvl.ctaGuide")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-14 md:py-20 bg-slate-50/60" data-testid="dvl-faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">{t("dvl.faqLabel")}</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{t("dvl.faqTitle")}</h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqs) && faqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-100 shadow-sm overflow-hidden" data-testid={`dvl-faq-${i}`}>
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dvl-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTEXTUAL LINKS */}
      <section className="py-10 md:py-14 bg-white" data-testid="dvl-contextual">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4 text-center">{t("dvNav.toolsLabel")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Link to={lp("/dv-lottery-dates")} data-testid="dvl-ctx-dates">
              <Button variant="outline" size="sm" className="font-medium border-slate-200 text-xs px-3 py-2 h-auto">
                <CalendarDays className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {t("dvNav.guides.1.title")}
              </Button>
            </Link>
            <Link to={lp("/dv-lottery-eligibility")} data-testid="dvl-ctx-elig">
              <Button variant="outline" size="sm" className="font-medium border-slate-200 text-xs px-3 py-2 h-auto">
                <ShieldCheck className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {t("dvNav.guides.7.title")}
              </Button>
            </Link>
            <Link to={lp("/how-to-fill-dv-lottery-form")} data-testid="dvl-ctx-form">
              <Button variant="outline" size="sm" className="font-medium border-slate-200 text-xs px-3 py-2 h-auto">
                <FileText className="mr-1.5 h-3.5 w-3.5 text-teal-600" /> {t("dvNav.guides.3.title")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/green-card-lottery" />

      {/* 10. FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dvl-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">{t("dvl.finalTitle")}</h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">{t("dvl.finalSubtitle")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/auth?mode=register")} data-testid="dvl-final-start">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-8 py-6 text-base">
                {t("dvl.ctaFinalStart")} <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#eligibility" data-testid="dvl-final-elig">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <ShieldCheck className="mr-2 h-4 w-4" /> {t("dvl.ctaFinalEligibility")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
